import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { messagesApi } from '../../../api';
import { useUIStore } from '../../../store';

const ServiceMessagesPage = ({ sess = { uid: '', role: 'user' } }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  const showSnackbar = useUIStore((state) => state.showSnackbar);

  useEffect(() => {
    fetchServiceMessages();
  }, []);

  useEffect(() => {
    if (status.type !== 'success' || !status.message) return;
    showSnackbar({ message: status.message, severity: 'success' });
    setStatus({ type: '', message: '' });
  }, [showSnackbar, status]);

  const fetchServiceMessages = async () => {
    try {
      setLoading(true);
      const response = await messagesApi.getServiceMessages({ uid: sess.uid });
      const list = response?.data?.data || response?.data || [];
      setMessages(list);
      setStatus({ type: '', message: '' });
    } catch (error) {
      setMessages([]);
      const message = error?.response?.data?.message || 'Failed to load service messages';
      setStatus({ type: 'error', message });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Typography variant="h5">Service Messages</Typography>
      </Stack>

      <Card>
        <CardContent>
          {status.message && status.type === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {status.message}
            </Alert>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : messages.length === 0 ? (
            <Typography align="center" color="text.secondary">
              No service messages found.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messages.map((msg, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{formatDate(msg.createdAt)}</TableCell>
                      <TableCell>{msg.from}</TableCell>
                      <TableCell>{msg.to}</TableCell>
                      <TableCell>
                        {msg.message?.substring(0, 50)}
                        {msg.message?.length > 50 ? '...' : ''}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={msg.status}
                          color="success"
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ServiceMessagesPage;
